<template>
  <div
    ref="slidebox"
    class="flex flex-wrap items-center transition-all duration-700 delay-300"
    :class="[
      !isTextLeft ? 'flex-row-reverse' : '',
      useChildrenSlideIn ? 'overflow-visible' : '',
      !useChildrenSlideIn && !show ? 'opacity-0' : '',
      !useChildrenSlideIn && show ? 'opacity-100' : '',
    ]"
  >
    <div
      class="w-full md:w-1/2 px-4 md:px-0 transition-all duration-500 delay-500"
      :class="[
        isTextLeft ? 'md:pr-8 md:ps-3' : 'md:pl-8 md:pe-3',
        useChildrenSlideIn && !show ? 'opacity-0 translate-y-12' : '',
        useChildrenSlideIn && show ? 'opacity-100 translate-y-0' : '',
      ]"
    >
      <div class="md:max-w-137.5" :class="isTextLeft ? 'md:ml-auto' : ''">
        <h2 class="mb-5 uppercase text-center md:text-left text-2xl">
          {{ component.slide.title }}
        </h2>
        <common-md-content
          v-once
          v-slot="{ renderedContent }"
          :content="component.slide.text"
        >
          <div class="text-center md:text-left" v-html="renderedContent" />
        </common-md-content>
      </div>
    </div>

    <div
      class="w-full md:w-1/2 relative transition-all duration-500 delay-1000"
      :class="[
        inContainer ? 'flex items-center' : 'h-64 sm:h-112 md:h-64 lg:h-112',
        useChildrenSlideIn && !show && isTextLeft
          ? 'opacity-0 translate-x-12'
          : '',
        useChildrenSlideIn && !show && !isTextLeft
          ? 'opacity-0 -translate-x-12'
          : '',
        useChildrenSlideIn && show ? 'opacity-100 translate-x-0' : '',
      ]"
    >
      <strapi-slide-box-slider
        v-if="images.length > 1"
        :images="images"
        class="h-full px-4 md:px-0"
      />
      <strapi-image
        v-else-if="images.length === 1"
        :image="images[0]"
        :alt="images[0].alternativeText"
        as-background
        class="rounded-none lg:rounded-md"
        :class="{
          'shadow-md': imageHasShadow,
          'w-full h-auto static max-h-96 object-contain': inContainer,
        }"
      />
      <strapi-image
        v-if="parallaxImage != null"
        :style="{ transform: translatePos }"
        :image="parallaxImage"
        :alt="parallaxImage.alternativeText"
        fluid
        class="absolute top-0 left-0"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  useElementBounding,
  useIntersectionObserver,
  useScroll,
} from '@vueuse/core';
import {
  type StrapiHomePageSlideBoxComponent,
  StrapiHomePageTextPosition,
} from '~/apollo/types/types';

const parallaxFactors: Record<string, [number, number]> = {
  games: [0, 2],
  online: [1.5, 1],
};

const props = withDefaults(
  defineProps<{
    component: StrapiHomePageSlideBoxComponent;
    inContainer?: boolean;
    imageHasShadow?: boolean;
    useChildrenSlideIn?: boolean;
    appearThreshold?: number;
  }>(),
  {
    inContainer: false,
    imageHasShadow: true,
    useChildrenSlideIn: false,
    appearThreshold: 0.5,
  },
);

const slidebox = ref<HTMLElement | null>(null);
const slideboxPos = ref(-1);
const show = ref(false);

const isTextLeft = computed(
  () => props.component.slide.textPosition === StrapiHomePageTextPosition.Left,
);

const images = computed(() => props.component.slide.images);
const parallaxImage = computed(() => props.component.slide.parallaxImage);

const { top, height } = useElementBounding(slidebox);
const translatePos = computed(() => {
  const factors = parallaxFactors[props.component.privateTitle];
  if (factors && show.value) {
    return `translate(${slideboxPos.value * factors[0]}px, ${slideboxPos.value * factors[1]}px)`;
  }
  return 'translate(0, 0)';
});

const updateScrollPos = () => {
  const scrollPos = top.value + height.value;
  const scrollLength = window.innerHeight + height.value;
  const percent = Math.floor((scrollPos / scrollLength) * 100);
  if (percent > 0 && percent <= 100) {
    slideboxPos.value = percent;
  }
};

if (import.meta.client) {
  useScroll(document, { throttle: 10, onScroll: updateScrollPos });
}

useIntersectionObserver(
  slidebox,
  ([{ isIntersecting }]) => {
    if (isIntersecting) show.value = true;
  },
  { threshold: props.appearThreshold },
);

onMounted(updateScrollPos);
</script>
